import { call, put, select } from 'redux-saga/effects';

import * as pageStateService from '../page-state/page-state.saga-services';
import * as pageInitializerService from '../page.initializer.saga-service';
import * as chartService from '../page-storage/chart/chart.saga-services';
import * as chartResourceService from '../../resource/chart/chart.saga-service';
import * as nodeResourceActions from '../../resource/node/node.actions';
import { chartSelectors } from '../../resource/chart/chart.selectors';
import { nodeSelectors } from '../../resource/node/node.selectors';
import * as chartPageActions from '../page-storage/chart/chart.actions';
import { chartPageSelectors } from '../page-storage/chart/chart.selectors';
import * as dialogService from '../../dialog/dialog.saga-services';
import { selectWithParams } from '../../../utils/selector.utils';
import { DEFAULT_CHART_TYPE } from './chart.constants';
import { IChart } from '../../resource/chart/chart.types';
import { IOrganizationChartNode } from '../../resource/node/node.types';

/** If there are more charts in the future, we can add a chartType parameter to the function. */
export function* initialChartPage() {
  yield call(pageStateService.initPage, 'chart');
  if (!((yield call(pageInitializerService.initPage)) as boolean)) {
    yield call(chartService.setupChart, DEFAULT_CHART_TYPE);
  }
  yield call(pageStateService.completedInit, 'chart');
}

export function* initialChartEditPage() {
  yield call(pageStateService.initPage, 'chartEdit');
  yield call(pageInitializerService.initPage);

  try {
    const chartType = DEFAULT_CHART_TYPE;
    const versions = (yield call(chartResourceService.listChartVersions, chartType)) as IChart[];
    yield put(chartPageActions.storeChartVersions(versions));
    yield put(chartPageActions.setDraftSelected(false));

    yield call(chartResourceService.getChart, chartType, { scope: 'draft' });

    const published = (yield call(
      chartResourceService.getChart,
      chartType,
    )) as chartResourceService.IChartWithNodesResult;

    let selectedChartId: IChart['id'] | null = published.chart?.id ?? null;

    if (published.chart) {
      yield call(storePreviewNodeIds, published.nodes ?? []);
    } else if (versions.length > 0) {
      selectedChartId = versions[0].id;
      const versionResult = (yield call(chartResourceService.getChart, chartType, {
        chartId: selectedChartId,
      })) as chartResourceService.IChartWithNodesResult;
      yield call(storePreviewNodeIds, versionResult.nodes);
    } else {
      yield call(storePreviewNodeIds, []);
    }

    yield put(chartPageActions.setSelectedChartId(selectedChartId));
  } finally {
    yield call(pageStateService.completedInit, 'chartEdit');
  }
}

export function* selectChartDraft() {
  const isDraftSelected = (yield select(chartPageSelectors.selectIsDraftSelected)) as boolean;
  if (isDraftSelected) {
    return;
  }

  const draft = (yield select(chartSelectors.selectDraftChart)) as IChart | null;
  if (!draft) {
    return;
  }

  const nodes = (yield selectWithParams(nodeSelectors.selectNodesByChartId, draft.id)) as IOrganizationChartNode[];

  yield put(chartPageActions.setSelectedChartId(draft.id));
  yield put(chartPageActions.setDraftSelected(true));
  yield call(storePreviewNodeIds, nodes);
}

export function* selectChartVersion(chartId: IChart['id']) {
  const currentSelected = (yield select(chartPageSelectors.selectSelectedChartId)) as IChart['id'] | null;
  const isDraftSelected = (yield select(chartPageSelectors.selectIsDraftSelected)) as boolean;
  if (!isDraftSelected && currentSelected === chartId) {
    const existingNodes = (yield selectWithParams(
      nodeSelectors.selectNodesByChartId,
      chartId,
    )) as IOrganizationChartNode[];
    yield call(storePreviewNodeIds, existingNodes);
    return;
  }

  const { chart, nodes } = (yield call(chartResourceService.getChart, DEFAULT_CHART_TYPE, {
    chartId,
  })) as chartResourceService.IChartWithNodesResult;

  yield put(chartPageActions.setDraftSelected(false));
  yield put(chartPageActions.setSelectedChartId(chart?.id ?? chartId));
  yield call(storePreviewNodeIds, nodes);
}

export function* publishSelectedChartVersion() {
  const selectedChartId = (yield select(chartPageSelectors.selectSelectedChartId)) as IChart['id'] | null;
  if (selectedChartId === null) {
    return;
  }

  const chartType = DEFAULT_CHART_TYPE;
  const result = (yield call(
    chartResourceService.publishChartVersion,
    chartType,
    selectedChartId,
  )) as chartResourceService.IChartWithNodesResult | null;

  if (!result) {
    return;
  }

  const versions = (yield call(chartResourceService.listChartVersions, chartType)) as IChart[];
  yield put(chartPageActions.storeChartVersions(versions));
  yield put(chartPageActions.setDraftSelected(false));
  yield put(chartPageActions.setSelectedChartId(result.chart?.id ?? selectedChartId));
  yield call(storePreviewNodeIds, result.nodes);
}

export function* publishSelectedChartDraft() {
  const draft = (yield select(chartSelectors.selectDraftChart)) as IChart | null;
  if (!draft) {
    return;
  }

  const chartType = DEFAULT_CHART_TYPE;
  const draftId = draft.id;
  const result = (yield call(
    chartResourceService.publishDraftChart,
    chartType,
  )) as chartResourceService.IChartWithNodesResult | null;

  if (!result) {
    return;
  }

  yield call(chartResourceService.clearChart, draftId);

  const versions = (yield call(chartResourceService.listChartVersions, chartType)) as IChart[];
  yield put(chartPageActions.storeChartVersions(versions));
  yield put(chartPageActions.setDraftSelected(false));
  yield put(chartPageActions.setSelectedChartId(result.chart?.id ?? null));
  yield call(storePreviewNodeIds, result.nodes);
  yield call(dialogService.closeDialog, 'confirmation');
}

export function* createEmptyChartDraft() {
  const chartType = DEFAULT_CHART_TYPE;
  const existingDraft = (yield select(chartSelectors.selectDraftChart)) as IChart | null;

  if (existingDraft) {
    const oldNodes = (yield selectWithParams(
      nodeSelectors.selectNodesByChartId,
      existingDraft.id,
    )) as IOrganizationChartNode[];
    if (oldNodes.length > 0) {
      yield put(nodeResourceActions.clearNode(oldNodes.map((node) => node.id)));
    }
    yield call(chartResourceService.clearChart, existingDraft.id);
  }

  const result = (yield call(
    chartResourceService.createEmptyDraft,
    chartType,
  )) as chartResourceService.IChartWithNodesResult | null;
  if (!result?.chart) {
    return;
  }

  yield put(chartPageActions.setSelectedChartId(result.chart.id));
  yield put(chartPageActions.setDraftSelected(true));
  yield call(storePreviewNodeIds, result.nodes);
  yield call(dialogService.closeDialog, 'confirmation');
}

export function* createDraftFromVersion() {
  const isDraftSelected = (yield select(chartPageSelectors.selectIsDraftSelected)) as boolean;
  const selectedChartId = (yield select(chartPageSelectors.selectSelectedChartId)) as IChart['id'] | null;
  if (isDraftSelected || selectedChartId === null) {
    return;
  }

  const chartType = DEFAULT_CHART_TYPE;
  const existingDraft = (yield select(chartSelectors.selectDraftChart)) as IChart | null;

  if (existingDraft) {
    const oldNodes = (yield selectWithParams(
      nodeSelectors.selectNodesByChartId,
      existingDraft.id,
    )) as IOrganizationChartNode[];
    if (oldNodes.length > 0) {
      yield put(nodeResourceActions.clearNode(oldNodes.map((node) => node.id)));
    }
    yield call(chartResourceService.clearChart, existingDraft.id);
  }

  const result = (yield call(
    chartResourceService.createDraftFromVersion,
    chartType,
    selectedChartId,
  )) as chartResourceService.IChartWithNodesResult | null;
  if (!result?.chart) {
    return;
  }

  yield put(chartPageActions.setSelectedChartId(result.chart.id));
  yield put(chartPageActions.setDraftSelected(true));
  yield call(storePreviewNodeIds, result.nodes);
  yield call(dialogService.closeDialog, 'confirmation');
}

export function* deleteChart(chartId: IChart['id']) {
  const chartType = DEFAULT_CHART_TYPE;
  const selectedChartId = (yield select(chartPageSelectors.selectSelectedChartId)) as IChart['id'] | null;
  const isDraftSelected = (yield select(chartPageSelectors.selectIsDraftSelected)) as boolean;
  const wasSelected = !isDraftSelected && String(selectedChartId) === String(chartId);

  const deleted = (yield call(chartResourceService.deleteChart, chartType, chartId)) as boolean;
  if (!deleted) {
    return;
  }

  const nodes = (yield selectWithParams(nodeSelectors.selectNodesByChartId, chartId)) as IOrganizationChartNode[];
  if (nodes.length > 0) {
    yield put(nodeResourceActions.clearNode(nodes.map((node) => node.id)));
  }

  yield call(chartResourceService.clearChart, chartId);

  const versions = (yield call(chartResourceService.listChartVersions, chartType)) as IChart[];
  yield put(chartPageActions.storeChartVersions(versions));

  if (!wasSelected) {
    yield call(dialogService.closeDialog, 'confirmation');
    return;
  }

  const publishedVersion = versions.find((version) => version.published);
  if (publishedVersion) {
    yield call(selectChartVersion, publishedVersion.id);
  } else if (versions.length > 0) {
    yield call(selectChartVersion, versions[0].id);
  } else {
    const draft = (yield select(chartSelectors.selectDraftChart)) as IChart | null;
    if (draft) {
      yield call(selectChartDraft);
    } else {
      yield put(chartPageActions.setSelectedChartId(null));
      yield put(chartPageActions.setDraftSelected(false));
      yield call(storePreviewNodeIds, []);
    }
  }

  yield call(dialogService.closeDialog, 'confirmation');
}

export function normalizeNodes(
  nodes: IOrganizationChartNode | IOrganizationChartNode[] | null | undefined,
): IOrganizationChartNode[] {
  if (!nodes) {
    return [];
  }
  return Array.isArray(nodes) ? nodes : [nodes];
}

function* storePreviewNodeIds(nodes: IOrganizationChartNode | IOrganizationChartNode[] | null | undefined) {
  const list = normalizeNodes(nodes);
  if (!list.length) {
    yield put(chartPageActions.storeChart([]));
    return;
  }

  yield put(chartPageActions.storeChart(list.map((node) => String(node.id))));
}
